export function setsEqual<T>(a: Set<T>, b: Set<T>): boolean {
  if (a === b) return true
  if (a.size !== b.size) return false
  for (const item of a) if (!b.has(item)) return false
  return true
}

/**
 * Helper type function that checks at a type level that every possible value is used. This helps to ensure that
 * the list of values is updated if the type ever changes - it's essentially allowing us to go from a type union to a
 * runtime `Set` in a safe manner.
 *
 * @example
 * type Value = "value1" | "value2" | "value3"
 *
 * const allValues = setOfAll<Value>()("value1", "value2", "value3")
 */
// This has to be a function that returns a function because TS won't let us define one function that has two type
// arguments where one is required and the other is inferred
export const setOfAll =
  <T>() =>
  <U extends T[]>(...values: U & ([T] extends [U[number]] ? unknown : 'Invalid')) =>
    new Set(values)

function removeTrailingSlash(url: string) {
  return url.endsWith('/') ? url.slice(0, url.length - 1) : url
}

/**
 * Check if `text` is a valid URL string.
 */
export function isUrl(text: string) {
  // Copied from https://github.com/github/paste-markdown/blob/c2673fc1ff5436b9035a09b78efdbae8a7469781/src/paste-markdown-link.ts#L72-L83
  try {
    // eslint-disable-next-line no-restricted-syntax
    const parsedUrl = new URL(text)
    // Ensures we don't allow things like `https://this will technically parse as a URL`
    return removeTrailingSlash(parsedUrl.href).trim() === removeTrailingSlash(text).trim()
  } catch {
    return false
  }
}
